import { useState } from "react";
import { 
  ShieldAlert, User, Building2, Send, X, AlertTriangle, 
  CheckCircle2, Clock, Activity, FileText, Sparkles, Loader2 
} from "lucide-react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/firebase";

const DIRECTIVE_PRESETS = [
  {
    id: "glycemia_screen",
    title: "Tamizaje de glicemia capilar en sitio",
    description: "Toma de glicemia capilar en ayuno presencial con glucómetro",
    defaultNote: "Colaborador presenta estancamiento en glicemia alterada. Favor realizar tamizaje capilar en ayuno antes del inicio de turno y reportar resultado.",
    priority: "high",
  },
  {
    id: "anthropometry_remeasure",
    title: "Re-medición antropométrica (cintura y peso)",
    description: "Control de somatometría, perímetro abdominal y ratio WHtR",
    defaultNote: "Re-medir perímetro abdominal en el punto medio axilar y registrar peso en báscula calibrada para evaluar variabilidad del índice cintura-talla.",
    priority: "normal",
  },
  {
    id: "nutrition_lifestyle",
    title: "Refuerzo nutricional y pausa activa",
    description: "Intervención en hábitos alimentarios y activación física",
    defaultNote: "Incentivar participación en el programa de pausas activas y coordinar entrega de pauta de hidratación y alimentación saludable con bienestar laboral.",
    priority: "normal",
  },
  {
    id: "endocrinology_eval",
    title: "Agendamiento con Endocrinología",
    description: "Cita médica prioritaria con la especialista invitada",
    defaultNote: "Derivación para evaluación clínica personalizada con la especialista en endocrinología programada para este ciclo de vigilancia.",
    priority: "urgent",
  },
  {
    id: "bp_holter",
    title: "Mapeo de Presión Arterial en Jornada",
    description: "Toma seriada de tensión arterial mañana y tarde",
    defaultNote: "Tomar presión arterial sistólica/diastólica en reposo al inicio y mitad de jornada durante 3 días hábiles consecutivos.",
    priority: "high",
  },
];

export default function IhoCoordinationModal({ isOpen, onClose, collaborator, indicators, trend, onDirectiveSent }) {
  const [selectedPreset, setSelectedPreset] = useState("glycemia_screen");
  const [priority, setPriority] = useState("high");
  const [notes, setNotes] = useState(DIRECTIVE_PRESETS[0].defaultNote);
  const [submitting, setSubmitting] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);

  if (!isOpen || !collaborator) return null;

  const handleSelectPreset = (preset) => {
    setSelectedPreset(preset.id);
    setPriority(preset.priority);
    setNotes(preset.defaultNote);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const directivePayload = {
      collaboratorUid: collaborator.uid || indicators.userUid,
      collaboratorName: indicators.name,
      collaboratorEmail: indicators.email,
      companyId: indicators.companyId,
      directiveType: selectedPreset,
      directiveTitle: DIRECTIVE_PRESETS.find(p => p.id === selectedPreset)?.title || "Directiva Médica",
      priority,
      notes,
      clinicalTrigger: trend?.label || "Monitoreo Preventivo",
      riskSummary: {
        imc: indicators.imc,
        whtr: indicators.whtr,
        glucemia: indicators.glucemia,
        pas: indicators.pas,
        pad: indicators.pad,
        stressIndex: indicators.stressIndex,
      },
      status: "pending_iho",
      createdAt: serverTimestamp(),
      createdDateString: new Date().toISOString(),
    };

    try {
      // Registrar en Firestore si no es colaborador simulado
      if (collaborator.uid && !collaborator.uid.startsWith("sim-emp-")) {
        await addDoc(collection(db, "users", collaborator.uid, "iho_directives"), directivePayload);
      }
    } catch (err) {
      console.warn("[IHO Modal] Modo offline o simulación activa:", err);
    }

    setSubmitting(false);
    setSentSuccess(true);

    if (onDirectiveSent) {
      onDirectiveSent(directivePayload);
    }

    setTimeout(() => {
      setSentSuccess(false);
      onClose();
    }, 1800);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl border border-stone-200 overflow-hidden flex flex-col max-h-[90vh]"
        style={{ background: "#FCFAEE" }}
      >
        {/* Cabecera de la Modal */}
        <div className="px-6 py-4 bg-gradient-to-r from-amber-600 via-amber-700 to-amber-800 text-white flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/25">
              <ShieldAlert size={20} className="text-amber-100" />
            </div>
            <div>
              <h3 className="text-base font-black tracking-tight">Directiva Médica para el IHO</h3>
              <p className="text-xs text-amber-100/90 font-medium">Coordinación clínica con el Oficial de Higiene y Salud Ocupacional</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl hover:bg-white/20 text-white/80 hover:text-white transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Contenido / Formulario */}
        <div className="p-6 overflow-y-auto space-y-5">
          {sentSuccess ? (
            <div className="py-12 text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center shadow-inner">
                <CheckCircle2 size={36} />
              </div>
              <h4 className="text-lg font-bold text-stone-900">Directiva Enviada con Éxito</h4>
              <p className="text-xs text-stone-600 max-w-md mx-auto">
                Se notificó al Oficial de Salud (IHO) de la empresa para ejecutar la intervención en sitio con <strong>{indicators.name}</strong>.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Tarjeta de Resumen del Colaborador */}
              <div className="p-4 rounded-2xl bg-white border border-stone-200/80 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center shrink-0 border border-amber-200/60 font-bold text-sm">
                    {indicators.name?.charAt(0) || "C"}
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-stone-900">{indicators.name}</h4>
                    <p className="text-xs text-stone-500">{indicators.email} · {indicators.edad} · {indicators.sexo}</p>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {indicators.imc && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-stone-100 text-stone-700 border border-stone-200">
                          IMC {indicators.imc}
                        </span>
                      )}
                      {indicators.whtr && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-stone-100 text-stone-700 border border-stone-200">
                          WHtR {indicators.whtr}
                        </span>
                      )}
                      {indicators.glucemia && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200">
                          Glu {indicators.glucemia} mg/dL
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Badge de Alerta Dinámica */}
                <div className="sm:text-right shrink-0">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${trend?.badgeClass || "bg-amber-100 text-amber-900 border-amber-300"}`}>
                    <span className="w-2 h-2 rounded-full" style={{ background: trend?.dotColor || "#D97706" }} />
                    {trend?.label || "Estancamiento ⚠️"}
                  </span>
                  <p className="text-[10px] text-stone-500 mt-1 max-w-[200px] leading-tight">
                    {trend?.detail || "Sin variación en parámetros de riesgo"}
                  </p>
                </div>
              </div>

              {/* Selector de Acción Clínica Solicitada */}
              <div>
                <label className="block text-xs font-bold text-stone-700 mb-2 uppercase tracking-wider">
                  Acción Clínica Solicitada al IHO:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {DIRECTIVE_PRESETS.map((p) => {
                    const isSelected = selectedPreset === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => handleSelectPreset(p)}
                        className={`p-3 rounded-2xl border text-left transition-all cursor-pointer ${
                          isSelected
                            ? "bg-amber-50/90 border-amber-500 shadow-sm ring-1 ring-amber-500/40"
                            : "bg-white hover:bg-stone-50 border-stone-200"
                        }`}
                      >
                        <p className={`text-xs font-bold leading-snug ${isSelected ? "text-amber-950" : "text-stone-800"}`}>
                          {p.title}
                        </p>
                        <p className="text-[11px] text-stone-500 mt-0.5 line-clamp-1 leading-normal">
                          {p.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Nivel de Prioridad */}
              <div>
                <label className="block text-xs font-bold text-stone-700 mb-2 uppercase tracking-wider">
                  Nivel de Prioridad Operativa:
                </label>
                <div className="flex gap-2">
                  {[
                    { id: "normal", label: "Informativa / Rutina", color: "text-slate-700 bg-slate-100 border-slate-300" },
                    { id: "high", label: "Prioritaria (48 horas)", color: "text-amber-800 bg-amber-100 border-amber-300" },
                    { id: "urgent", label: "Urgente (Mismo día)", color: "text-rose-800 bg-rose-100 border-rose-300" },
                  ].map((lvl) => {
                    const isSelected = priority === lvl.id;
                    return (
                      <button
                        key={lvl.id}
                        type="button"
                        onClick={() => setPriority(lvl.id)}
                        className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                          isSelected ? `${lvl.color} ring-2 ring-amber-600/30 shadow-sm` : "bg-white text-stone-600 border-stone-200"
                        }`}
                      >
                        {lvl.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Campo de Instrucciones Médicas Detalladas */}
              <div>
                <label className="block text-xs font-bold text-stone-700 mb-1.5 uppercase tracking-wider">
                  Instrucciones Clínicas Específicas:
                </label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Detalle los puntos clave para el IHO (ej. hora de la toma, pautas de ayuno, recomendación)..."
                  className="w-full p-3 rounded-2xl bg-white border border-stone-300 text-xs font-medium text-stone-800 outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all shadow-sm"
                  required
                />
              </div>

              {/* Botonera de Envío */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-stone-200">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="px-4 py-2.5 rounded-xl text-xs font-bold text-stone-600 hover:bg-stone-200/60 transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 shadow-md shadow-amber-900/10 transition-all cursor-pointer active:scale-95 disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>Notificando al IHO...</span>
                    </>
                  ) : (
                    <>
                      <Send size={14} />
                      <span>Emitir Directiva a IHO</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
